import { isApiError } from '@saay/api-client';
import { MOBILE_E164_PATTERN, normalizeMobileField, statusLabel } from '@saay/formatting';
import {
  Button,
  Checkbox,
  Combobox,
  EmptyState,
  Masked,
  NativeSelect,
  SearchField,
  SegmentedButtons,
  Skeleton,
  Switch,
  TextArea,
  TextField,
  useSearchInput,
  useToast,
} from '../kit.js';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Controller, type UseFormRegisterReturn, type UseFormReturn } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';

import type {
  AdminBuyerSummary,
  Organization,
  OrganizationCreate,
  OrganizationMembership,
  OrganizationMembershipCreate,
  OrganizationMembershipUpdate,
  OrganizationRole,
  OrganizationRoleCreate,
  OrganizationUpdate,
} from '../api.js';
import { CommandDialog } from '../components/command-dialog.js';
import { normalizeAdminCode } from '../components/form.js';
import { QueryState } from '../components/guard.js';
import { GeographyFields } from '../components/pickers.js';
import {
  organizationPath,
  setupKeys,
  useActiveOrganizationRoles,
  useBuyers,
  useCities,
  useOrganizationRoles,
} from '../queries.js';
import { reasonSchema } from '../validation.js';
import { suggestOrganizationCode } from './organization-code.js';
import { FormReasonChips } from '../components/reason-field.js';

/**
 * Organization commands (AC §18.8 O2, O4, O6, O7, O9; FTD §9.5): create and edit an organization, the
 * global role catalog, and memberships. Each result is the server's record; the drawer re-reads it.
 */

/**
 * Organization types: a free code label (AC §18.8 `organizationType`, `^[A-Z][A-Z0-9_]{1,31}$`), never
 * a merge key. The form offers the prototype's types; a stored code without a label shows as is.
 */
export const ORGANIZATION_TYPES: Readonly<Record<string, string>> = {
  CLUB: 'باشگاه و گیم‌نت',
  CORPORATE: 'شرکتی',
  EDUCATION: 'آموزشی',
  OTHER: 'سایر',
};

export function organizationTypeLabel(code: string): ReactNode {
  return ORGANIZATION_TYPES[code] ?? 'سایر';
}

export function useRefreshOrganization(organizationId?: string) {
  const queryClient = useQueryClient();
  return async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: setupKeys.organizations }),
      queryClient.invalidateQueries({ queryKey: setupKeys.summaries }),
      queryClient.invalidateQueries({ queryKey: setupKeys.organizationRoles }),
      ...(organizationId === undefined
        ? []
        : [queryClient.invalidateQueries({ queryKey: setupKeys.organization(organizationId) })]),
    ]);
  };
}

/** The recovery line of a refused organization command, by error code only (AC §18.8, §13). */
export function organizationGuidance(error: unknown): ReactNode {
  if (!isApiError(error)) return undefined;
  const details = error.details ?? {};
  switch (error.code) {
    case 'STATE_CONFLICT':
      return typeof details.currentStatus === 'string'
        ? `وضعیت فعلی: ${statusLabel('activeStatus', details.currentStatus)}. سازمان غیرفعال عضو یا ثبت‌نام جدید نمی‌پذیرد.`
        : 'اطلاعات به‌روز شد.';
    case 'RESOURCE_VERSION_CONFLICT':
      return 'این مورد در این فاصله تغییر کرده است؛ اطلاعات به‌روز شد. دوباره ثبت کنید.';
    case 'ORGANIZATION_MEMBERSHIP_EXISTS':
      return 'این خریدار عضویت فعالی در همین سازمان دارد؛ نقش آن را از فهرست اعضا تغییر دهید.';
    default:
      return undefined;
  }
}

const ORGANIZATION_CODE = /^[A-Z0-9][A-Z0-9-]{1,31}$/;
const CODE_LABEL = /^[A-Z][A-Z0-9_]{1,31}$/;

const optionalMobile = z
  .string()
  .transform((value) => (value.trim() === '' ? '' : normalizeMobileField(value)))
  .refine(
    (value) => value === '' || MOBILE_E164_PATTERN.test(value),
    'شماره موبایل معتبر وارد کنید.',
  );

const organizationSchema = z.object({
  cityId: z.string().min(1, 'شهر را انتخاب کنید.'),
  contactMobileE164: optionalMobile,
  contactNameFa: z.string().trim().max(200, 'حداکثر ۲۰۰ کاراکتر.'),
  displayNameFa: z
    .string()
    .trim()
    .min(1, 'نام سازمان را وارد کنید.')
    .max(200, 'حداکثر ۲۰۰ کاراکتر.'),
  guarantorExempt: z.boolean(),
  organizationCode: z
    .string()
    .transform(normalizeAdminCode)
    .refine(
      (value) => ORGANIZATION_CODE.test(value),
      'حروف لاتین بزرگ، رقم و خط تیره؛ مثل ORG-EDU-01.',
    ),
  organizationType: z
    .string()
    .refine((value) => CODE_LABEL.test(value), 'نوع سازمان را انتخاب کنید.'),
  provinceId: z.string().min(1, 'استان را انتخاب کنید.'),
  reason: z.string(),
  status: z.enum(['ACTIVE', 'INACTIVE']),
});
type OrganizationValues = z.input<typeof organizationSchema>;

const updateSchema = organizationSchema.extend({ reason: reasonSchema() });

function OrganizationFields({
  editing,
  form,
}: {
  readonly editing: Organization | undefined;
  readonly form: UseFormReturn<OrganizationValues, unknown, OrganizationValues>;
}) {
  const errors = form.formState.errors;
  const organizationType = form.watch('organizationType');
  const provinceId = form.watch('provinceId');
  const cityId = form.watch('cityId');
  const cities = useCities(provinceId === '' ? undefined : provinceId);
  const cityName =
    cities.data?.find((city) => city.id === cityId)?.nameFa ??
    (editing?.city.id === cityId ? editing.city.nameFa : '');
  const codeTouched = form.formState.dirtyFields.organizationCode === true;
  useEffect(() => {
    if (editing !== undefined || organizationType === '' || cityName === '' || codeTouched) {
      return;
    }
    form.setValue('organizationCode', suggestOrganizationCode(organizationType, cityName), {
      shouldDirty: false,
      shouldValidate: true,
    });
  }, [cityName, codeTouched, editing, form, organizationType]);
  const typeOptions = useMemo(() => {
    const entries = Object.entries(ORGANIZATION_TYPES).map(([value, label]) => ({
      key: value,
      label,
    }));
    if (
      editing !== undefined &&
      ORGANIZATION_TYPES[editing.organizationType] === undefined &&
      editing.organizationType !== ''
    ) {
      entries.push({ key: editing.organizationType, label: editing.organizationType });
    }
    return entries;
  }, [editing]);
  return (
    <>
      <div className="admin-grid-2">
        <TextField
          error={errors.displayNameFa?.message}
          label="نام رسمی سازمان"
          {...form.register('displayNameFa')}
        />
        <TextField
          disabled={editing !== undefined}
          error={errors.organizationCode?.message}
          label="کد مستقل سازمان"
          ltr
          {...form.register('organizationCode', {
            onChange: (event) => {
              event.target.value = normalizeAdminCode(event.target.value);
            },
          })}
        />
        <Controller
          control={form.control}
          name="organizationType"
          render={({ field }) => (
            <>
              <SegmentedButtons
                label="نوع سازمان"
                onChange={field.onChange}
                options={typeOptions}
                value={field.value}
              />
              {errors.organizationType?.message === undefined ? null : (
                <span className="saay-field__error" role="alert">
                  {errors.organizationType.message}
                </span>
              )}
            </>
          )}
        />
        <TextField
          error={errors.contactNameFa?.message}
          label="نماینده پاسخ‌گو (اختیاری)"
          {...form.register('contactNameFa')}
        />
        <TextField
          autoComplete="off"
          error={errors.contactMobileE164?.message}
          {...(editing !== undefined && editing.contactMobileE164 === undefined
            ? { hint: 'شماره فعلی پنهان است؛ برای تغییر، شماره جدید را وارد کنید.' }
            : {})}
          inputMode="tel"
          label="شماره تماس نماینده (اختیاری)"
          ltr
          {...form.register('contactMobileE164')}
        />
        {editing === undefined ? null : (
          <NativeSelect
            label="وضعیت"
            options={(['ACTIVE', 'INACTIVE'] as const).map((value) => ({
              label: statusLabel('activeStatus', value),
              value,
            }))}
            {...form.register('status')}
          />
        )}
      </div>
      <Controller
        control={form.control}
        name="provinceId"
        render={({ field: province }) => (
          <Controller
            control={form.control}
            name="cityId"
            render={({ field: city }) => (
              <GeographyFields
                cityError={errors.cityId?.message}
                cityId={city.value}
                idPrefix="organization"
                onCityChange={city.onChange}
                onProvinceChange={province.onChange}
                provinceError={errors.provinceId?.message}
                provinceId={province.value}
              />
            )}
          />
        )}
      />
      <Controller
        control={form.control}
        name="guarantorExempt"
        render={({ field }) => (
          <Switch
            checked={field.value === true}
            label="معاف از ضامن در ثبت‌نام"
            onCheckedChange={field.onChange}
          />
        )}
      />
      {editing === undefined ? null : (
        <>
          <TextArea
            error={errors.reason?.message}
            label="دلیل تغییر"
            maxLength={1000}
            {...form.register('reason')}
          />
          <FormReasonChips form={form} intent="organization." />
        </>
      )}
    </>
  );
}

const ORGANIZATION_FIELDS = [
  'organizationCode',
  'displayNameFa',
  'organizationType',
  'provinceId',
  'cityId',
  'contactNameFa',
  'contactMobileE164',
  // `guarantorExempt` and `status` show no message of their own: their errors are listed.
  'reason',
];

/** O2 create (→ the new drawer) or O4 update (only the changed fields). */
export function OrganizationDialog({
  editing,
  onOpenChange,
}: {
  readonly editing?: Organization;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const refresh = useRefreshOrganization(editing?.id);
  const defaults: OrganizationValues = {
    cityId: editing?.city.id ?? '',
    contactMobileE164: editing?.contactMobileE164 ?? '',
    contactNameFa: editing?.contactNameFa ?? '',
    displayNameFa: editing?.displayNameFa ?? '',
    guarantorExempt: editing?.guarantorExempt ?? true,
    organizationCode: editing?.organizationCode ?? '',
    organizationType: editing?.organizationType ?? '',
    provinceId: editing?.province.id ?? '',
    reason: '',
    status: editing?.status ?? 'ACTIVE',
  };
  const onError = async (
    error: { readonly code: string; readonly messageFa: string },
    form: UseFormReturn<OrganizationValues, unknown, OrganizationValues>,
  ) => {
    if (error.code === 'ORGANIZATION_CODE_DUPLICATE') {
      form.setError('organizationCode', { message: error.messageFa, type: 'server' });
    }
    if (error.code === 'GEOGRAPHY_MISMATCH') {
      form.setError('cityId', { message: error.messageFa, type: 'server' });
    }
    if (error.code === 'RESOURCE_VERSION_CONFLICT') await refresh();
  };
  if (editing === undefined) {
    return (
      <CommandDialog<OrganizationValues, OrganizationCreate, Organization>
        defaultValues={defaults}
        fields={ORGANIZATION_FIELDS}
        guidance={organizationGuidance}
        name="organization.create"
        onError={onError}
        onOpenChange={onOpenChange}
        onSuccess={async (result) => {
          await refresh();
          toast.success({ confirmedBy: result, title: 'سازمان ساخته شد' });
          void navigate(`/organizations/${result.data.id}/enrollments/new`, { replace: true });
        }}
        schema={organizationSchema as never}
        send={(client, body, intent) => client.post('/admin/organizations', { body, intent })}
        size="wide"
        submitLabel="ساخت سازمان"
        title="تعریف سازمان جدید"
        toBody={(values) => {
          const parsed = organizationSchema.parse(values);
          return {
            cityId: parsed.cityId,
            displayNameFa: parsed.displayNameFa,
            guarantorExempt: parsed.guarantorExempt,
            organizationCode: parsed.organizationCode,
            organizationType: parsed.organizationType,
            provinceId: parsed.provinceId,
            ...(parsed.contactNameFa === '' ? {} : { contactNameFa: parsed.contactNameFa }),
            ...(parsed.contactMobileE164 === ''
              ? {}
              : { contactMobileE164: parsed.contactMobileE164 }),
          };
        }}
      >
        {(form) => <OrganizationFields editing={undefined} form={form} />}
      </CommandDialog>
    );
  }
  return (
    <CommandDialog<OrganizationValues, OrganizationUpdate, Organization>
      defaultValues={defaults}
      description="غیرفعال‌کردن سازمان عضویت‌ها و اعتبار داده‌شده را نگه می‌دارد؛ اما عضو، ثبت‌نام یا هدف‌گذاری جدید نمی‌پذیرد."
      eyebrow={editing.displayNameFa}
      fields={ORGANIZATION_FIELDS}
      guidance={organizationGuidance}
      name="organization.update"
      onError={onError}
      onOpenChange={onOpenChange}
      onSuccess={async (result) => {
        await refresh();
        toast.success({ confirmedBy: result, title: 'سازمان به‌روز شد' });
        onOpenChange(false);
      }}
      schema={updateSchema as never}
      send={(client, body) => client.patch(organizationPath(editing.id), { body })}
      size="wide"
      submitLabel="ذخیره تغییرات"
      title="ویرایش سازمان"
      toBody={(values) => {
        const parsed = updateSchema.parse(values);
        const changes: Partial<OrganizationUpdate> = {};
        if (parsed.displayNameFa !== editing.displayNameFa)
          changes.displayNameFa = parsed.displayNameFa;
        if (parsed.organizationType !== editing.organizationType) {
          changes.organizationType = parsed.organizationType;
        }
        if (parsed.provinceId !== editing.province.id || parsed.cityId !== editing.city.id) {
          changes.provinceId = parsed.provinceId;
          changes.cityId = parsed.cityId;
        }
        if (parsed.contactNameFa !== (editing.contactNameFa ?? '')) {
          changes.contactNameFa = parsed.contactNameFa === '' ? null : parsed.contactNameFa;
        }
        // A hidden mobile (Auditor, Marketing) is sent only when a new one is typed.
        const currentMobile = editing.contactMobileE164 ?? '';
        if (
          parsed.contactMobileE164 !== currentMobile &&
          !(editing.contactMobileE164 === undefined && parsed.contactMobileE164 === '')
        ) {
          changes.contactMobileE164 =
            parsed.contactMobileE164 === '' ? null : parsed.contactMobileE164;
        }
        if (parsed.guarantorExempt !== editing.guarantorExempt) {
          changes.guarantorExempt = parsed.guarantorExempt;
        }
        if (parsed.status !== editing.status) changes.status = parsed.status;
        return Object.keys(changes).length === 0
          ? null
          : { ...changes, expectedVersion: editing.version, reason: parsed.reason };
      }}
    >
      {(form) => <OrganizationFields editing={editing} form={form} />}
    </CommandDialog>
  );
}

// ---------------------------------------------------------------------------------------------
// O9 createOrganizationRole

const roleSchema = z.object({
  category: z.string().trim().max(64, 'حداکثر ۶۴ کاراکتر.'),
  nameFa: z.string().trim().min(1, 'عنوان نقش را وارد کنید.').max(100, 'حداکثر ۱۰۰ کاراکتر.'),
  roleCode: z
    .string()
    .transform(normalizeAdminCode)
    .refine((value) => CODE_LABEL.test(value), 'حروف لاتین بزرگ و زیرخط؛ مثل TEACHER.'),
});
type RoleValues = z.input<typeof roleSchema>;

export function OrganizationRoleDialog({
  onOpenChange,
}: {
  readonly onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const refresh = useRefreshOrganization();
  const roles = useOrganizationRoles({ sort: 'roleCode' });
  const categoryOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const page of roles.data?.pages ?? []) {
      for (const role of page.items) {
        if (role.category !== null && role.category.trim() !== '') {
          seen.add(role.category.trim());
        }
      }
    }
    return [...seen]
      .sort((a, b) => a.localeCompare(b, 'fa'))
      .map((value) => ({ label: value, value }));
  }, [roles.data]);
  return (
    <CommandDialog<RoleValues, OrganizationRoleCreate, OrganizationRole>
      defaultValues={{ category: '', nameFa: '', roleCode: '' }}
      fields={['nameFa', 'roleCode', 'category']}
      name="organization-role.create"
      onError={(error, form) => {
        if (error.code === 'ORGANIZATION_ROLE_CODE_DUPLICATE') {
          form.setError('roleCode', { message: error.messageFa, type: 'server' });
        }
      }}
      onOpenChange={onOpenChange}
      onSuccess={async (result) => {
        await refresh();
        toast.success({ confirmedBy: result, title: `نقش «${result.data.nameFa}» ثبت شد` });
        onOpenChange(false);
      }}
      schema={roleSchema as never}
      send={(client, body, intent) => client.post('/admin/organization-roles', { body, intent })}
      submitLabel="ثبت نقش"
      title="تعریف نقش سراسری"
      toBody={(values) => {
        const parsed = roleSchema.parse(values);
        return {
          nameFa: parsed.nameFa,
          roleCode: parsed.roleCode,
          ...(parsed.category === '' ? {} : { category: parsed.category }),
        };
      }}
    >
      {(form) => (
        <>
          <TextField
            error={form.formState.errors.nameFa?.message}
            label="عنوان فارسی"
            {...form.register('nameFa')}
          />
          <TextField
            error={form.formState.errors.roleCode?.message}
            label="کد ثابت"
            ltr
            {...form.register('roleCode', {
              onChange: (event) => {
                event.target.value = normalizeAdminCode(event.target.value);
              },
            })}
          />
          <Controller
            control={form.control}
            name="category"
            render={({ field, fieldState }) => (
              <Combobox
                error={fieldState.error?.message}
                label="دسته"
                onChange={(value) => field.onChange(value ?? '')}
                onInputValueChange={(next) => field.onChange(next)}
                options={categoryOptions}
                placeholder="جست‌وجو یا دستهٔ جدید"
                value={field.value === '' ? undefined : field.value}
              />
            )}
          />
        </>
      )}
    </CommandDialog>
  );
}

// ---------------------------------------------------------------------------------------------
// O6 createOrganizationMembership, O7 updateOrganizationMembership

/** A buyer search by name (≥ 3 letters) over `listAdminBuyers`; the picked buyer is the value. */
export function BuyerPicker({
  error,
  onChange,
  value,
}: {
  readonly error?: string | undefined;
  readonly onChange: (buyer: AdminBuyerSummary | null) => void;
  readonly value: AdminBuyerSummary | null;
}) {
  const [query, setQuery] = useState('');
  const search = useSearchInput({
    applied: query,
    invalidMessage: 'برای جست‌وجو دست‌کم ۳ حرف وارد کنید.',
    minLength: 3,
    onApply: setQuery,
  });
  const buyers = useBuyers({ q: query }, query.length >= 3);
  const rows = buyers.data?.pages.flatMap((page) => page.items) ?? [];
  if (value !== null) {
    return (
      <div className="admin-inline">
        <strong>{`${value.firstNameFa} ${value.lastNameFa}`}</strong>
        <Masked kind="mobile" value={value.mobileMasked} />
        <Button onClick={() => onChange(null)} size="sm" variant="ghost">
          تغییر خریدار
        </Button>
      </div>
    );
  }
  return (
    <div className="admin-form">
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          search.submit();
        }}
      >
        <SearchField
          invalid={search.message !== undefined}
          label="جست‌وجوی خریدار"
          onChange={(event) => search.setText(event.target.value)}
          placeholder="نام خریدار"
          value={search.text}
        />
      </form>
      {search.message === undefined ? null : <p className="admin-muted">{search.message}</p>}
      {error === undefined ? null : (
        <span className="saay-field__error" role="alert">
          {error}
        </span>
      )}
      {query.length < 3 ? null : (
        <QueryState
          empty={<EmptyState title="خریداری با این نام پیدا نشد" />}
          error={buyers.error}
          isEmpty={rows.length === 0}
          onRetry={() => void buyers.refetch()}
          skeleton={<Skeleton label="در حال جست‌وجو…" lines={2} />}
          status={buyers.status}
        >
          <ul className="admin-plain-list">
            {rows.slice(0, 10).map((buyer) => (
              <li key={buyer.buyerId}>
                <Button onClick={() => onChange(buyer)} size="sm" variant="ghost">
                  {`${buyer.firstNameFa} ${buyer.lastNameFa}`}
                </Button>{' '}
                <Masked kind="mobile" value={buyer.mobileMasked} />{' '}
                <Masked kind="nationalCode" value={buyer.nationalCodeMasked} />
              </li>
            ))}
          </ul>
        </QueryState>
      )}
    </div>
  );
}

export function RolePicker({
  error,
  id,
  label = 'نقش سراسری',
  register,
}: {
  readonly error?: string | undefined;
  readonly id: string;
  readonly label?: string;
  readonly register: UseFormRegisterReturn<'organizationRoleId'>;
}) {
  const roles = useActiveOrganizationRoles();
  return (
    <NativeSelect
      error={error}
      hint={
        roles.status === 'pending'
          ? 'در حال بارگذاری نقش‌ها…'
          : roles.status === 'error'
            ? 'فهرست نقش‌ها بارگذاری نشد.'
            : undefined
      }
      id={id}
      label={label}
      options={(roles.data ?? []).map((role) => ({
        label: `${role.nameFa} · ${role.roleCode}`,
        value: role.id,
      }))}
      placeholder="انتخاب نقش"
      {...register}
    />
  );
}

const membershipSchema = z.object({
  buyer: z
    .custom<AdminBuyerSummary | null>()
    .refine((value) => value !== null, 'خریدار را انتخاب کنید.'),
  localTitleFa: z.string().trim().max(100, 'حداکثر ۱۰۰ کاراکتر.'),
  organizationRoleId: z.string().min(1, 'نقش را انتخاب کنید.'),
});
type MembershipValues = z.input<typeof membershipSchema>;

export function MembershipCreateDialog({
  onOpenChange,
  organization,
}: {
  readonly onOpenChange: (open: boolean) => void;
  readonly organization: Organization;
}) {
  const toast = useToast();
  const refresh = useRefreshOrganization(organization.id);
  return (
    <CommandDialog<MembershipValues, OrganizationMembershipCreate, OrganizationMembership>
      defaultValues={{ buyer: null, localTitleFa: '', organizationRoleId: '' }}
      description="یک خریدار می‌تواند عضو چند سازمان باشد؛ اعتبار سازمانی فقط از راه هدف‌گذاری برنامه روی همان کارت می‌نشیند."
      eyebrow={organization.displayNameFa}
      // The server names `buyerId`; the person picked a buyer (`buyer`).
      fieldAliases={{ buyerId: 'buyer' }}
      fields={['organizationRoleId', 'localTitleFa']}
      guidance={organizationGuidance}
      name="organization.membership.create"
      onError={async (error) => {
        if (error.code === 'STATE_CONFLICT') await refresh();
      }}
      onOpenChange={onOpenChange}
      onSuccess={async (result) => {
        await refresh();
        toast.success({
          confirmedBy: result,
          title: `${result.data.buyer.displayNameFa} عضو سازمان شد`,
        });
        onOpenChange(false);
      }}
      schema={membershipSchema as never}
      send={(client, body, intent) =>
        client.post(organizationPath(organization.id, '/memberships'), { body, intent })
      }
      size="wide"
      submitLabel="ثبت عضویت"
      title="افزودن عضو سازمان"
      toBody={(values) => ({
        buyerId: (values.buyer as AdminBuyerSummary).buyerId,
        organizationRoleId: values.organizationRoleId,
        ...(values.localTitleFa.trim() === '' ? {} : { localTitleFa: values.localTitleFa.trim() }),
      })}
    >
      {(form) => (
        <>
          <Controller
            control={form.control}
            name="buyer"
            render={({ field, fieldState }) => (
              <BuyerPicker
                error={fieldState.error?.message}
                onChange={field.onChange}
                value={field.value}
              />
            )}
          />
          <RolePicker
            error={form.formState.errors.organizationRoleId?.message}
            id="membership-role"
            register={form.register('organizationRoleId')}
          />
          <TextField
            error={form.formState.errors.localTitleFa?.message}
            hint="مثلاً دبیر ریاضی"
            label="عنوان محلی (اختیاری)"
            {...form.register('localTitleFa')}
          />
        </>
      )}
    </CommandDialog>
  );
}

const membershipUpdateSchema = z.object({
  deactivate: z.boolean(),
  localTitleFa: z.string().trim().max(100, 'حداکثر ۱۰۰ کاراکتر.'),
  organizationRoleId: z.string().min(1, 'نقش را انتخاب کنید.'),
  reason: reasonSchema(),
});
type MembershipUpdateValues = z.input<typeof membershipUpdateSchema>;

export function MembershipUpdateDialog({
  membership,
  onOpenChange,
}: {
  readonly membership: OrganizationMembership;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const refresh = useRefreshOrganization(membership.organizationId);
  return (
    <CommandDialog<MembershipUpdateValues, OrganizationMembershipUpdate, OrganizationMembership>
      defaultValues={{
        deactivate: false,
        localTitleFa: membership.localTitleFa ?? '',
        organizationRoleId: membership.role.id,
        reason: '',
      }}
      description="پایان عضویت به اعتبار داده‌شده دست نمی‌زند (D-34) و قابل برگشت نیست؛ عضویت دوباره یک عضویت جدید است."
      eyebrow={membership.buyer.displayNameFa}
      // `status` has no message of its own on the «پایان عضویت» checkbox: it is listed under the form.
      fields={['organizationRoleId', 'localTitleFa', 'reason']}
      guidance={organizationGuidance}
      name="organization.membership.update"
      onError={async (error) => {
        if (error.code === 'RESOURCE_VERSION_CONFLICT' || error.code === 'STATE_CONFLICT') {
          await refresh();
        }
      }}
      onOpenChange={onOpenChange}
      onSuccess={async (result) => {
        await refresh();
        toast.success({
          confirmedBy: result,
          title: result.data.status === 'INACTIVE' ? 'عضویت پایان یافت' : 'عضویت به‌روز شد',
        });
        onOpenChange(false);
      }}
      schema={membershipUpdateSchema as never}
      send={(client, body) =>
        client.patch(`/admin/organization-memberships/${encodeURIComponent(membership.id)}`, {
          body,
        })
      }
      submitLabel="ثبت تغییر"
      title="ویرایش عضویت"
      toBody={(values) => {
        const changes: Partial<OrganizationMembershipUpdate> = {};
        if (values.organizationRoleId !== membership.role.id) {
          changes.organizationRoleId = values.organizationRoleId;
        }
        const title = values.localTitleFa.trim();
        if (title !== (membership.localTitleFa ?? ''))
          changes.localTitleFa = title === '' ? null : title;
        if (values.deactivate) changes.status = 'INACTIVE';
        return Object.keys(changes).length === 0
          ? null
          : { ...changes, expectedVersion: membership.version, reason: values.reason.trim() };
      }}
    >
      {(form) => (
        <>
          <RolePicker
            error={form.formState.errors.organizationRoleId?.message}
            id="membership-edit-role"
            register={form.register('organizationRoleId')}
          />
          <TextField
            error={form.formState.errors.localTitleFa?.message}
            label="عنوان محلی (اختیاری)"
            {...form.register('localTitleFa')}
          />
          <Checkbox label="پایان عضویت" {...form.register('deactivate')} />
          <TextArea
            error={form.formState.errors.reason?.message}
            label="دلیل تغییر"
            maxLength={1000}
            {...form.register('reason')}
          />
          <FormReasonChips form={form} intent="organization." />
        </>
      )}
    </CommandDialog>
  );
}
